import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/lib/supabase';
import { uploadMedia } from '@/lib/cloudflare';
import { Globe, Lock, Users, Image, X, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface GroupForm {
  name: string;
  slug: string;
  description: string;
  visibility: 'public' | 'private';
  requires_approval: boolean;
  max_members: string;
  avatar_image?: File;
  cover_image?: File;
}

export function CreateGroup() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState<GroupForm>({
    name: '',
    slug: '',
    description: '',
    visibility: 'public',
    requires_approval: false,
    max_members: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);

  const handleImageChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
    type: 'avatar' | 'cover'
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size should be less than 5MB');
        return;
      }

      try {
        // Upload to Cloudflare R2
        const url = await uploadMedia(file);
        
        // Update form and preview
        if (type === 'avatar') {
          setAvatarPreview(URL.createObjectURL(file));
          setForm(prev => ({ ...prev, avatar_image: file }));
        } else {
          setCoverPreview(URL.createObjectURL(file));
          setForm(prev => ({ ...prev, cover_image: file }));
        }
      } catch (error) {
        console.error('Error uploading image:', error);
        setError('Failed to upload image. Please try again.');
      }
    }
  };

  const generateUniqueSlug = async (baseSlug: string): Promise<string> => {
    let slug = baseSlug;
    let counter = 1;
    let isUnique = false;

    while (!isUnique) {
      const { data } = await supabase
        .from('groups')
        .select('slug')
        .eq('slug', slug)
        .single();

      if (!data) {
        isUnique = true;
      } else {
        slug = `${baseSlug}-${counter}`;
        counter++;
      }
    }

    return slug;
  };

  const handleNameChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    const baseSlug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    
    setForm(prev => ({
      ...prev,
      name,
      slug: baseSlug // Set initial slug, will be made unique on submit
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    
    setLoading(true);
    setError(null);

    try {
      // Generate unique slug
      const uniqueSlug = await generateUniqueSlug(form.slug);

      // Create the group
      const { data: group, error: groupError } = await supabase
        .from('groups')
        .insert({
          name: form.name,
          slug: uniqueSlug,
          description: form.description,
          visibility: form.visibility,
          requires_approval: form.requires_approval,
          max_members: form.max_members ? parseInt(form.max_members) : null,
          creator_id: user.id,
          avatar_url: avatarPreview,
          cover_image: coverPreview
        })
        .select()
        .single();

      if (groupError) throw groupError;

      navigate(`/groups/${group.id}`);
    } catch (error) {
      console.error('Error creating group:', error);
      setError(error instanceof Error ? error.message : 'Failed to create group');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="bg-white dark:bg-dark-paper rounded-xl shadow-sm">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Header */}
          <div className="p-6 border-b dark:border-dark-border">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Create Group</h1>
          </div>

          <div className="p-6 space-y-6">
            {error && (
              <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">
                {error}
              </div>
            )}

            {/* Images */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Avatar
                </label>
                <div className="flex items-center gap-4">
                  {avatarPreview ? (
                    <div className="relative w-24 h-24">
                      <img
                        src={avatarPreview}
                        alt="Avatar preview"
                        className="w-full h-full rounded-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setAvatarPreview(null);
                          setForm(prev => ({ ...prev, avatar_image: undefined }));
                        }}
                        className="absolute -top-1 -right-1 p-1 bg-black/50 rounded-full text-white hover:bg-black/70 transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <label className="w-24 h-24 flex flex-col items-center justify-center border-2 border-dashed border-gray-300 dark:border-dark-border rounded-full cursor-pointer hover:border-royal-purple transition-colors">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageChange(e, 'avatar')}
                        className="hidden"
                      />
                      <Image className="w-6 h-6 text-gray-400 dark:text-gray-500" />
                      <span className="text-xs text-gray-500 dark:text-gray-400 mt-1">Upload</span>
                    </label>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Cover Image
                </label>
                {coverPreview ? (
                  <div className="relative rounded-lg overflow-hidden h-48">
                    <img
                      src={coverPreview}
                      alt="Cover preview"
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setCoverPreview(null);
                        setForm(prev => ({ ...prev, cover_image: undefined }));
                      }}
                      className="absolute top-2 right-2 p-1 bg-black/50 rounded-full text-white hover:bg-black/70 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center h-48 border-2 border-dashed border-gray-300 dark:border-dark-border rounded-lg cursor-pointer hover:border-royal-purple transition-colors">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageChange(e, 'cover')}
                      className="hidden"
                    />
                    <div className="space-y-2 text-center">
                      <div className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500">
                        <Image className="w-12 h-12" />
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        <span className="font-medium text-royal-purple">
                          Click to upload
                        </span>
                        {' or drag and drop'}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        PNG, JPG, GIF up to 5MB
                      </p>
                    </div>
                  </label>
                )}
              </div>
            </div>

            {/* Basic Info */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Group Name
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={handleNameChange}
                  required
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-royal-purple focus:border-transparent dark:bg-dark-elevated dark:border-dark-border dark:text-gray-100"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm(prev => ({
                    ...prev,
                    description: e.target.value
                  }))}
                  rows={4}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-royal-purple focus:border-transparent dark:bg-dark-elevated dark:border-dark-border dark:text-gray-100"
                />
              </div>
            </div>

            {/* Settings */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Settings</h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Visibility
                </label>
                <div className="space-y-2">
                  <label className="flex items-center gap-3 p-3 border dark:border-dark-border rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-dark-elevated transition-colors">
                    <input
                      type="radio"
                      name="visibility"
                      value="public"
                      checked={form.visibility === 'public'}
                      onChange={(e) => setForm(prev => ({
                        ...prev,
                        visibility: e.target.value as 'public'
                      }))}
                      className="w-4 h-4 text-royal-purple focus:ring-royal-purple dark:bg-dark-elevated dark:border-dark-border"
                    />
                    <div>
                      <div className="flex items-center gap-2">
                        <Globe className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                        <span className="font-medium text-gray-900 dark:text-gray-100">Public</span>
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        Anyone can see and join this group
                      </p>
                    </div>
                  </label>

                  <label className="flex items-center gap-3 p-3 border dark:border-dark-border rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-dark-elevated transition-colors">
                    <input
                      type="radio"
                      name="visibility"
                      value="private"
                      checked={form.visibility === 'private'}
                      onChange={(e) => setForm(prev => ({
                        ...prev,
                        visibility: e.target.value as 'private'
                      }))}
                      className="w-4 h-4 text-royal-purple focus:ring-royal-purple dark:bg-dark-elevated dark:border-dark-border"
                    />
                    <div>
                      <div className="flex items-center gap-2">
                        <Lock className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                        <span className="font-medium text-gray-900 dark:text-gray-100">Private</span>
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        Only members can see and access this group
                      </p>
                    </div>
                  </label>
                </div>
              </div>

              <div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={form.requires_approval}
                    onChange={(e) => setForm(prev => ({
                      ...prev,
                      requires_approval: e.target.checked
                    }))}
                    className="w-4 h-4 rounded text-royal-purple focus:ring-royal-purple dark:bg-dark-elevated dark:border-dark-border"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Require admin approval for new members
                  </span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Maximum Members
                </label>
                <input
                  type="number"
                  min="1"
                  value={form.max_members}
                  onChange={(e) => setForm(prev => ({
                    ...prev,
                    max_members: e.target.value
                  }))}
                  placeholder="Leave empty for unlimited"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-royal-purple focus:border-transparent dark:bg-dark-elevated dark:border-dark-border dark:text-gray-100"
                />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 dark:bg-dark-elevated border-t dark:border-dark-border">
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-paper rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className={cn(
                  "px-4 py-2 rounded-lg font-medium transition-colors",
                  loading
                    ? "bg-gray-300 dark:bg-gray-700 cursor-not-allowed"
                    : "bg-royal-purple text-white hover:bg-royal-purple/90"
                )}
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Creating...</span>
                  </div>
                ) : (
                  'Create Group'
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}